import React from 'react';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import CloseIcon from '@mui/icons-material/Close';
import {
  SettingsIcon,
  ProfileIconRounded,
  WorkspacesIcon,
  BillingIcon,
  MarketplaceIcon,
  ConnectionsIcon,
} from '../SVG';
import MenuButton from '../MenuButton';
import AccountIcon from 'components/SVG/AccountIcon';
import { useCurrentUser } from 'hooks/common/useCurrentUser';
import { useAccountUsers } from 'hooks/Account/useAccountUsers';
import { useAccountRoles } from 'hooks/EntityTypes/useAccountRoles';
import { useResize, useWorkspaceContext } from 'hooks/common';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton } from '@mui/material';

const primaryListItem = [
  {
    id: 1,
    name: 'My Profile',
    path: '/settings/my-profile',
    icon: <ProfileIconRounded />,
  },
  {
    id: 2,
    name: 'Account',
    path: '/settings/account',
    icon: <AccountIcon />,
  },
  {
    id: 3,
    name: 'Workspaces',
    path: '/settings/Workspaces',
    icon: <WorkspacesIcon />,
  },
  {
    id: 4,
    name: 'Connections',
    path: '/settings/connections',
    icon: <ConnectionsIcon />,
  },
  {
    id: 5,
    name: 'Billing & Usage',
    path: '/settings/billing-and-usage',
    icon: <BillingIcon />,
  },
  {
    id: 6,
    name: 'Creator Profile',
    path: '/settings/creator',
    icon: <MarketplaceIcon />,
  },
];

const drawerWidth = '15rem';

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),

  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  [theme.breakpoints.down('sm')]: {
    width: 0,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',

  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

type SettingsSideNavProps = {
  open: boolean;
  toggleDrawer: () => void;
  headerHeight: number;
};

export default function SettingsSideNav({
  open,
  toggleDrawer,
  headerHeight,
}: SettingsSideNavProps) {
  const navigate = useNavigate();
  const { currentUserID } = useCurrentUser();
  const { getWorkspace } = useWorkspaceContext();
  const workspace = getWorkspace();
  const { accountUsers } = useAccountUsers(workspace?.attributes.accountID);
  const { idMap: accountRolesIdMap } = useAccountRoles();
  const { isMobile } = useResize();

  const handleSelect = (path: string) => {
    navigate(path);
    toggleDrawer();
  };

  const currentUserAccountRoleID = accountUsers?.find(
    (accountUser) => accountUser.attributes.userID === currentUserID
  )?.attributes.accountRoleID;

  const isAdmin = React.useMemo(() => {
    return (
      accountRolesIdMap?.[currentUserAccountRoleID || '']?.attributes.name ===
        'Admin' || false
    );
  }, [accountRolesIdMap, currentUserAccountRoleID]);

  return (
    <Drawer
      variant='permanent'
      open={isMobile ? open : true}
      sx={{
        '& .MuiDrawer-paper': {
          borderRight: (theme) => `1px solid ${theme.palette.borderColor}`,
          marginTop: isMobile ? 0 : `${headerHeight}px`,
          height: isMobile ? '100vh' : `calc(100vh - ${headerHeight}px)`,
        },
      }}
    >
      {isMobile && (
        <Box display='flex' justifyContent='flex-end'>
          <IconButton onClick={toggleDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}
      <List>
        {primaryListItem.map(({ name, id, icon, path }) => (
          <MenuButton
            key={id}
            open={true}
            text={name}
            path={path}
            icon={icon}
            onSelect={handleSelect}
          />
        ))}
        {isAdmin && (
          <MenuButton
            open={true}
            text='Admin'
            path='/settings/admin'
            icon={<SettingsIcon />}
            onSelect={handleSelect}
          />
        )}
      </List>
    </Drawer>
  );
}
